import React from "react";
import { EmptyState, Icon, Portal, StatCard, lostReasonLabel, confirmDialog, notify, DataTable, useLocalPref, clickable, downloadExport, estimateRoom, limitPrompt, LimitNote } from "./registry";
import { FencePlanSheet } from "./fenceplan";
import { downscaleToDataUrl } from "./lib/img";
import { newId } from "./lib/ids";
// EST-02: the document is doing one thing, the money another
import { docState, payState, DOC_LABEL, sendsOf, lastSentOf, inFollowUp } from "./lib/quotestate";
// a quote past its valid-until date can't be signed — flagged here, with a one-tap extension
import { openExpiry, extendValidUntil, fmtDate, businessTz, EXTEND_DAYS } from "./lib/quotedetails";
import { VERTICALS, inLens, measureOf, fmtMeasure, perUnitLabel, verticalOf } from "./lib/verticals";   // the lens: which service's estimates, and their unit
/* ============================================================
   Fence Works — Estimates, Permit Check (Wisconsin), Messages
   ============================================================ */
const MP_money0 = window.VFP.money0;
const EST_WON = ["signed", "paid"];   // matches WON_STATUSES in reports.tsx

/* ---- Quick add / scan-a-note: type an estimate in (no drawing), or photo a
   handwritten quote and let AI prefill the same form for review. Creating
   always goes through the contractor's eyes — the scan never auto-saves. ---- */
function QuickEstimateModal({ store, go, mode, onClose }: any) {
  const products = window.VFP.PRODUCTS || [];
  const [f, setF] = React.useState<any>({ customer: "", phone: "", email: "", address: "", productId: (products[0] || {}).id, lf: "", gates: "", override: "", notes: "" });
  const set = (patch: any) => setF((x: any) => ({ ...x, ...patch }));
  const [scanBusy, setScanBusy] = React.useState<any>(false);
  const [scanMsg, setScanMsg] = React.useState<any>("");
  const fileRef = React.useRef<any>(null);
  const product = products.find((p: any) => p.id === f.productId) || products[0];

  // live engine price for what's typed so far (override wins on the base line)
  const lfNum = Math.max(0, Number(f.lf) || 0);
  const gateCount = Math.max(0, Math.round(Number(f.gates) || 0));
  let computed: any = null;
  try { if (product && lfNum > 0) computed = window.VFP.computeQuote({ linearFeet: lfNum, gates: gateCount, manual: true }, product, store.settings || {}); } catch (e) {}
  /* What the customer actually sees if the fence line is overridden to `base`.
     Mirrors quote.tsx's re-foot: the customer-facing add-on lines and the slope
     line ride on top, then tax. Keeps this preview and the document in step. */
  const round2 = (n: number) => Math.round((Number(n) || 0) * 100) / 100;
  const ridersOnTop = computed
    ? round2((computed.addons || []).filter((a: any) => a.customerFacing).reduce((t: number, a: any) => t + (Number(a.amount) || 0), 0)
        + (computed.slopeAmount > 0 ? Number(computed.slopeAmount) || 0 : 0))
    : 0;
  function projectedTotal(base: number) {
    const sub = round2(base + ridersOnTop);
    return round2(sub + round2(sub * Math.max(0, (computed && Number(computed.taxRate)) || 0)));
  }
  const overrideNum = Number(f.override) > 0 ? round2(Number(f.override)) : null;

  async function scan(file: File) {
    setScanBusy(true); setScanMsg("Reading the note…");
    try {
      const img = await downscaleToDataUrl(file, 1600, 0.85);
      const DB: any = window.VFP_DB;
      const r = await DB.scanQuote(img);
      const x = (r && r.fields) || {};
      const match = x.fenceType ? products.find((p: any) => (p.name + " " + p.category).toLowerCase().includes(String(x.fenceType).toLowerCase().split(" ")[0])) : null;
      set({
        customer: x.customer || f.customer, phone: x.phone || f.phone, email: x.email || f.email, address: x.address || f.address,
        productId: (match && match.id) || f.productId,
        lf: x.linearFeet != null ? String(x.linearFeet) : f.lf,
        gates: x.gates != null ? String(x.gates) : f.gates,
        override: x.total != null ? String(x.total) : f.override,
        notes: [f.notes, x.notes, x.fenceType && !match ? "Fence type on note: " + x.fenceType : null].filter(Boolean).join("\n"),
      });
      setScanMsg("✓ Read it — double-check every field before creating");
    } catch (e: any) { setScanMsg("Couldn't read it: " + String((e && e.message) || e).slice(0, 90)); }
    finally { setScanBusy(false); }
  }

  /* EST-05: a backdrop click used to throw the form away without a word —
     including a scanned note the AI had just filled in. */
  const touched = !!(String(f.customer || "").trim() || String(f.phone || "").trim() || String(f.email || "").trim()
    || String(f.address || "").trim() || String(f.lf || "").trim() || String(f.override || "").trim() || String(f.notes || "").trim());
  async function tryClose() {
    if (touched) {
      const ok = await confirmDialog({
        title: "Discard this estimate?",
        body: `You've entered details for ${String(f.customer || "").trim() || "this customer"}. Closing loses them.`,
        confirmLabel: "Discard", cancelLabel: "Keep editing", danger: true,
      });
      if (!ok) return;
    }
    onClose();
  }

  function create() {
    if (!(f.customer || "").trim() || !(lfNum > 0) || !product) return;
    // plan cap: the month's estimates are used up — ask to upgrade, keep the form
    if (estimateRoom(store).atLimit) { limitPrompt(store, go, "estimates"); return; }
    const override = Number(f.override) > 0 ? Math.round(Number(f.override) * 100) / 100 : null;
    // The override replaces the fence line only — gates, permit/travel add-ons,
    // slope and tax still foot on top of it on the document. Storing the raw
    // number as q.total made the list read $5,000 for a quote the customer
    // receives as $5,600. Project the same re-footing the doc does instead.
    const total = override != null ? projectedTotal(override) : (computed ? computed.customerTotal : 0);
    const q: any = {
      id: newId("q"), customer: f.customer.trim(), phone: f.phone.trim(), email: f.email.trim(), address: f.address.trim(),
      lf: lfNum, productId: product.id, productName: product.name, status: "pending", createdAt: Date.now(),
      inputs: { linearFeet: lfNum, gates: gateCount, manual: true, quoteNotes: f.notes || undefined },   // the doc reads inputs.quoteNotes
      total,
    };
    if (override != null) q.lineOverrides = { base: override };   // hand-written price wins; add-ons/tax refoot on the doc
    if (!store.addQuote(q)) return;
    onClose();
    go("quote", { quoteId: q.id });
  }

  return (
    <Portal>
    <div className="modal-scrim" onClick={tryClose}>
      <div className="modal" style={{ width: "min(560px, 96vw)" }} onClick={(e: any) => e.stopPropagation()}>
        <h3 className="display" style={{ fontSize: 18, margin: "0 0 4px" }}>{mode === "scan" ? "Scan a handwritten quote" : "Quick estimate"}</h3>
        <p className="muted" style={{ fontSize: 12.5, margin: "0 0 12px" }}>
          {mode === "scan"
            ? "Photo the paper note — AI fills the form below, you fix anything it misread, then create."
            : "Type it straight in — no drawing needed. You can open it in the Estimator later to map the yard."}
        </p>
        <div className="row gap-8" style={{ marginBottom: 12, alignItems: "center", flexWrap: "wrap" }}>
          <input ref={fileRef} type="file" accept="image/*" capture="environment" style={{ display: "none" }}
            onChange={e => { const file = e.target.files && e.target.files[0]; if (file) scan(file); e.currentTarget.value = ""; }} />
          <button className={"btn btn-sm " + (mode === "scan" ? "btn-primary" : "btn-outline")} disabled={scanBusy} onClick={() => fileRef.current && fileRef.current.click()}>
            <Icon name="image" size={13} />{scanBusy ? "Reading…" : "Photo / upload the note"}
          </button>
          {scanMsg && <span className="muted" style={{ fontSize: 12 }}>{scanMsg}</span>}
        </div>
        <div style={{ display: "grid", gap: 10 }}>
          <div className="row gap-8" style={{ flexWrap: "wrap" }}>
            <div style={{ flex: "2 1 200px" }}><label className="field-label">Customer</label>
              <input className="input" value={f.customer} placeholder="Name" onChange={e => set({ customer: e.target.value })} /></div>
            <div style={{ flex: "1 1 140px" }}><label className="field-label">Phone</label>
              <input className="input" value={f.phone} onChange={e => set({ phone: e.target.value })} /></div>
          </div>
          <div className="row gap-8" style={{ flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 180px" }}><label className="field-label">Email</label>
              <input className="input" value={f.email} onChange={e => set({ email: e.target.value })} /></div>
            <div style={{ flex: "2 1 220px" }}><label className="field-label">Address</label>
              <input className="input" value={f.address} onChange={e => set({ address: e.target.value })} /></div>
          </div>
          <div className="row gap-8" style={{ flexWrap: "wrap" }}>
            <div style={{ flex: "2 1 200px" }}><label className="field-label">Product</label>
              <select className="select" value={f.productId || ""} onChange={e => set({ productId: e.target.value })}>
                {products.map((p: any) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select></div>
            <div><label className="field-label">Linear ft</label>
              <input className="input tnum" type="number" min="1" style={{ width: 100 }} value={f.lf} onChange={e => set({ lf: e.target.value })} /></div>
            <div><label className="field-label">Gates</label>
              <input className="input tnum" type="number" min="0" style={{ width: 80 }} value={f.gates} onChange={e => set({ gates: e.target.value })} /></div>
          </div>
          <div className="row gap-8" style={{ flexWrap: "wrap", alignItems: "flex-end" }}>
            <div><label className="field-label">Fence installation $ <span className="faint">(before gates, add-ons &amp; tax — optional)</span></label>
              <input className="input tnum" type="number" min="0" style={{ width: 140 }} value={f.override} placeholder={computed ? String(Math.round(computed.base)) : ""} onChange={e => set({ override: e.target.value })} /></div>
            {computed && <span className="muted" style={{ fontSize: 12.5, paddingBottom: 8 }}>
              engine price: <b className="tnum" style={{ color: "var(--teal)" }}>{MP_money0(computed.customerTotal)}</b>
              {lfNum > 0 && <> · {MP_money0(computed.customerTotal / lfNum)}/ft</>}
            </span>}
          </div>
          {/* what the customer will actually be sent — shown before creating, so a
              hand-written fence price can't quietly become a bigger quote */}
          {computed && overrideNum != null && (
            <div className="qa-total-preview">
              <div className="row between"><span className="muted">Fence installation (your number)</span><span className="tnum">{MP_money0(overrideNum)}</span></div>
              {ridersOnTop > 0 && <div className="row between"><span className="muted">Gates &amp; add-ons</span><span className="tnum">+ {MP_money0(ridersOnTop)}</span></div>}
              {computed.taxRate > 0 && <div className="row between"><span className="muted">{computed.taxLabel || "Sales tax"} · {(computed.taxRate * 100).toFixed(2).replace(/\.?0+$/, "")}%</span><span className="tnum">+ {MP_money0(projectedTotal(overrideNum) - overrideNum - ridersOnTop)}</span></div>}
              <div className="row between qa-total-row"><span>Customer total</span><span className="tnum">{MP_money0(projectedTotal(overrideNum))}</span></div>
            </div>
          )}
          <div><label className="field-label">Notes</label>
            <textarea className="input" rows={2} value={f.notes} onChange={e => set({ notes: e.target.value })} /></div>
        </div>
        <div className="row gap-8" style={{ marginTop: 14 }}>
          <button className="btn btn-primary btn-sm" disabled={!(f.customer || "").trim() || !(lfNum > 0)} onClick={create}><Icon name="check" size={13} />Create estimate</button>
          <button className="btn btn-ghost btn-sm" onClick={tryClose}>Cancel</button>
        </div>
      </div>
    </div>
    </Portal>
  );
}

/* EST-01: one entry point, three methods. Scan a note / Quick add / New
   Estimate competed in the header as three equal buttons, so the first
   decision on the page was which button — before you had told it anything.
   The split button leads with the method you used last and keeps the other two
   one tap away; nothing is hidden behind a "more". */
const NEW_METHODS = [
  { id: "map", label: "Draw on map", icon: "map", sub: "Trace the yard — the lot outlines itself" },
  { id: "manual", label: "Enter measurements", icon: "edit", sub: "Type footage, product and price. No drawing." },
  { id: "scan", label: "Scan paper estimate", icon: "image", sub: "Photo a handwritten note — AI fills the form for review" },
];
function NewEstimate({ onPick }: any) {
  const [pref, setPref] = useLocalPref("fw_est_method", "map");
  const [open, setOpen] = React.useState<any>(false);
  const wrapRef = React.useRef<any>(null);
  React.useEffect(() => {
    if (!open) return;
    const away = (e: any) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const esc = (e: any) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", away); document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", away); document.removeEventListener("keydown", esc); };
  }, [open]);
  const cur = NEW_METHODS.find(m => m.id === pref) || NEW_METHODS[0];
  const pick = (id: string) => { setPref(id); setOpen(false); onPick(id); };
  return (
    <span className="new-est" ref={wrapRef}>
      <button className="btn btn-primary new-est-main" onClick={() => pick(cur.id)} title={cur.sub}><Icon name="plus" size={15} />New estimate<span className="faint-on">· {cur.label}</span></button>
      <button className="btn btn-primary new-est-caret" aria-haspopup="menu" aria-expanded={!!open} aria-label="Choose how to build the estimate" onClick={() => setOpen((o: any) => !o)}><Icon name="chevdown" size={14} /></button>
      {open && (
        <div className="new-est-menu" role="menu">
          {NEW_METHODS.map(m => (
            <button key={m.id} role="menuitem" className={"new-est-item" + (m.id === pref ? " on" : "")} onClick={() => pick(m.id)}>
              <Icon name={m.icon} size={15} />
              <span><b>{m.label}</b><span className="faint">{m.sub}</span></span>
              {m.id === pref && <Icon name="check" size={13} className="new-est-tick" />}
            </button>
          ))}
        </div>
      )}
    </span>
  );
}

/* The two facts, side by side. Document state says where this is with the
   customer; payment state says how much of it is money. A quote can be
   Accepted with a Balance due, and neither implies the other — one "paid"
   status used to cover a deposit and a settled invoice alike. */
function QuoteState({ q, settings, lost }: any) {
  const d = docState(q);
  const pay = payState(q, settings);
  const dc: any = { draft: "var(--fg-3)", sent: "var(--blue)", viewed: "var(--cyan)", accepted: "var(--violet)", declined: "var(--red)", expired: "var(--amber)" };
  const pc: any = { "deposit-due": "var(--amber)", "deposit-paid": "var(--cyan)", "balance-due": "var(--amber)", paid: "var(--green)" };
  return (
    <span className="qstate">
      <span className="chip" style={{ color: dc[d], borderColor: "var(--line-2)", fontSize: 10.5 }}>
        <span style={{ width: 6, height: 6, borderRadius: 99, background: dc[d] }} />{DOC_LABEL[d]}
        {d === "declined" && lost ? <span className="muted"> · {lost}</span> : null}
        {d === "draft" ? <span className="muted"> · not sent</span> : null}
      </span>
      {pay.state !== "none" && (
        <span className="chip" style={{ color: pc[pay.state], borderColor: "var(--line-2)", fontSize: 10.5 }} title={`${MP_money0(pay.paid)} of ${MP_money0(pay.total)} collected`}>
          {pay.label}{pay.state === "balance-due" || pay.state === "deposit-paid" ? " · " + MP_money0(pay.balance) : ""}
        </span>
      )}
    </span>
  );
}

/* ---------------- Estimates page ---------------- */
function EstimatesPage({ store, go }: any) {
  const [showArchived, setShowArchived] = React.useState<any>(false);
  // the service lens (lib/verticals.ts): fence for everyone but a platform
  // admin who switched, so this filter is a no-op on every fence account
  const lens = store.lens || "fence";
  const V = VERTICALS[lens] || VERTICALS.fence;
  const allQuotes = (store.quotes || []).filter((q: any) => inLens(q, lens)).slice().reverse();
  const archivedCount = allQuotes.filter((q: any) => q.archivedAt).length;
  const quotes = showArchived ? allQuotes : allQuotes.filter((q: any) => !q.archivedAt);
  const [filter, setFilter] = React.useState<any>("all");
  const [packetQ, setPacketQ] = React.useState<any>(null);   // job-packet overlay for a row
  const [quick, setQuick] = React.useState<any>(null);       // "manual" | "scan" — quick-add modal
  const [view, setView] = useLocalPref("fw_est_view", "list"); // cards | list — remembered per device
  const [query, setQuery] = React.useState<any>("");
  /* EST-04: the filters were the raw status values, which is the database's
     vocabulary, not the contractor's. These are the four questions actually
     asked of this page, plus the statuses for anyone who wants them. */
  const VIEWS = [
    { id: "all", label: "All" },
    { id: "draft", label: "Drafts", hint: "Started, never sent" },
    { id: "followup", label: "Needs follow-up", hint: "Sent 3+ days ago with no answer" },
    { id: "signature", label: "Awaiting signature", hint: "Seen by the customer, not accepted" },
    { id: "accepted", label: "Accepted", hint: "Signed or paid" },
    { id: "expired", label: "Expired", hint: "Past its valid-until date and not signed — extend it so they can sign" },
    { id: "lost", label: "Lost" },
  ];
  const expiredQ = (q: any) => openExpiry(q, store.settings).expired;
  /* one tap: good for two more weeks, counted from today when it has already
     run out (lib/quotedetails.ts extendValidUntil) */
  function extendQuote(q: any) {
    const v = extendValidUntil(q.validUntil, EXTEND_DAYS, { tz: businessTz(store.settings) });
    store.updateQuote(q.id, { validUntil: v });
    store.addActivity(`Extended ${q.customer || "the customer"}'s quote — good until ${fmtDate(v)}`);
    notify(`${q.customer || "The quote"}: good until ${fmtDate(v)}.`);
  }
  const ExtendBtn = ({ q }: any) => (
    <button className="btn btn-outline btn-sm est-extend" title={`Good for ${EXTEND_DAYS} more days — the customer can sign it again`}
      onClick={(e) => { e.stopPropagation(); extendQuote(q); }}><Icon name="calendar" size={13} />Extend {EXTEND_DAYS} days</button>
  );
  // the Expired flag on a quote its document state doesn't already call expired (a draft)
  const ExpiredChip = ({ q }: any) => {
    const ex = openExpiry(q, store.settings);
    if (!ex.expired || docState(q) === "expired") return null;
    return <span className="chip est-expired-chip" title="Past its valid-until date — nobody can sign it until you extend it">Expired {fmtDate(ex.validUntil, ex.today)}</span>;
  };
  const STATS = ["draft", "pending", "viewed", "signed", "paid", "lost"];
  // EST-02: "pending" used to mean both "sent, waiting" and "never sent". They
  // are different jobs of work, so they are different filters, and a draft is
  // not counted as awaiting anything.
  const isDraft = (q: any) => docState(q) === "draft";
  const counts = STATS.reduce((m, s) => (m[s] = s === "draft"
    ? quotes.filter(isDraft).length
    : quotes.filter(q => q.status === s && !isDraft(q)).length, m), {} as any);
  const colors = { pending: "var(--fg-3)", viewed: "var(--blue)", signed: "var(--violet)", paid: "var(--green)", lost: "var(--red)" };
  const DAY = 86400000;
  const inView = (q: any) => {
    const d = docState(q);
    switch (filter) {
      case "all": return true;
      case "draft": return d === "draft";
      case "followup": { const t = lastSentOf(q); return inFollowUp(q) && t != null && Date.now() - t >= 3 * DAY; }
      case "signature": return d === "sent" || d === "viewed";
      case "accepted": return d === "accepted";
      case "lost": return d === "declined";
      case "expired": return expiredQ(q);
      default: return q.status === filter && d !== "draft";
    }
  };
  const needle = String(query || "").trim().toLowerCase();
  const matches = (q: any) => !needle || [q.customer, q.address, q.productName, q.rep].some((v: any) => String(v || "").toLowerCase().includes(needle));
  const list = quotes.filter((q: any) => inView(q) && matches(q));
  const countOf = (id: string) => quotes.filter((q: any) => {
    const d = docState(q);
    if (id === "all") return true;
    if (id === "draft") return d === "draft";
    if (id === "followup") { const t = lastSentOf(q); return inFollowUp(q) && t != null && Date.now() - t >= 3 * DAY; }
    if (id === "signature") return d === "sent" || d === "viewed";
    if (id === "accepted") return d === "accepted";
    if (id === "lost") return d === "declined";
    if (id === "expired") return expiredQ(q);
    return q.status === id && d !== "draft";
  }).length;
  // won = signed or paid (same convention as Reports / the leaderboard); open
  // pipeline = anything that can still turn into money — won is banked, lost
  // is dead; neither belongs in the number you chase this week.
  const pipelineValue = quotes.filter(q => !EST_WON.includes(q.status) && q.status !== "lost").reduce((a, q) => a + (q.total || 0), 0);
  const wonValue = quotes.filter(q => EST_WON.includes(q.status)).reduce((a, q) => a + (q.total || 0), 0);
  const fmt = (t) => new Date(t).toLocaleDateString("en-US", { month: "short", day: "numeric" });

  /* EST-04: an estimate a customer signed or paid is a business record, and
     Delete was sitting in the row next to Packet. A draft nobody ever sent is
     just clutter and can go. Anything in between is archived, not destroyed. */
  const removeLabel = (q: any) => docState(q) === "draft" ? "Delete" : "Archive";
  async function removeQuote(q: any) {
    const who = q.customer || "this customer";
    if (docState(q) === "draft") {
      const ok = await confirmDialog({ title: `Delete the draft for ${who}?`, body: "It was never sent, so nothing is lost but the draft.", confirmLabel: "Delete draft", danger: true });
      if (ok) store.deleteQuote(q.id);
      return;
    }
    const accepted = docState(q) === "accepted";
    const ok = await confirmDialog({
      title: `Archive the estimate for ${who}?`,
      body: accepted
        ? `${who} accepted this one${payState(q, store.settings).paid > 0 ? ` and has paid ${MP_money0(payState(q, store.settings).paid)}` : ""}. Archiving takes it off this list and out of follow-up; the record, the signed document and the payments stay. Reports still count it.`
        : "Archiving takes it off this list and out of follow-up. The record stays, and you can bring it back.",
      confirmLabel: "Archive it", cancelLabel: "Keep it here",
    });
    if (!ok) return;
    store.updateQuote(q.id, { archivedAt: Date.now() });
  }

  return (
    <div className="page fade-up">
      <div className="row between" style={{ marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
        <div><h1 className="display" style={{ fontSize: 28, margin: 0 }}>Estimates</h1>
          <p className="muted" style={{ margin: "4px 0 0" }}>Every quote you've sent — track status, follow up, and close.</p>
          <LimitNote store={store} go={go} kind="estimates" /></div>
        <div className="row gap-8" style={{ flexWrap: "wrap" }}>
          <div className="seg view-seg" role="tablist" aria-label="View">
            {[["cards", "Cards"], ["list", "List"]].map(([v, lb]) => (
              <button key={v} className={"seg-btn" + (view === v ? " on" : "")} onClick={() => setView(v)}>{lb}</button>
            ))}
          </div>
          {allQuotes.length > 0 && <button className="btn btn-ghost" onClick={() => downloadExport("estimates", store)} title="Download every estimate as a CSV — number, customer, status, total and the sent / signed dates"><Icon name="download" size={15} />Export CSV</button>}
          {/* plan cap: past this month's estimates, any method asks to upgrade first.
              Under the Concrete lens there is one method — the typed estimate (concrete.tsx) */}
          {lens === "concrete"
            ? <button className="btn btn-primary" onClick={() => estimateRoom(store).atLimit ? limitPrompt(store, go, "estimates") : go("estimator")}><Icon name="plus" size={15} />{V.newEstimate}</button>
            : <NewEstimate onPick={(m: string) => estimateRoom(store).atLimit ? limitPrompt(store, go, "estimates") : m === "map" ? go("estimator") : setQuick(m)} />}
        </div>
      </div>

      <div className="stat-row est-stats" style={{ marginBottom: 18 }}>
        <StatCard icon="send" value={quotes.length} label="Total Estimates" accent="var(--teal)" />
        <StatCard icon="bolt" value={(counts.pending || 0) + (counts.viewed || 0)} label="Awaiting Response" accent="var(--amber)" />
        {counts.draft > 0 && <StatCard icon="edit" value={counts.draft} label="Drafts — not sent" accent="var(--fg-3)" />}
        <StatCard icon="dollar" value={MP_money0(pipelineValue)} label="Open Pipeline" accent="var(--cyan)" />
        <StatCard icon="check" value={MP_money0(wonValue)} label="Won" accent="var(--green)" />
      </div>

      <div className="est-toolbar">
        <div className="cat-tabs" role="tablist" aria-label="Estimate views">
          {VIEWS.map(v => {
            const n = countOf(v.id);
            return <button key={v.id} role="tab" aria-selected={filter === v.id} title={v.hint}
              className={"filter-chip" + (filter === v.id ? " on" : "")} onClick={() => setFilter(v.id)}>{v.label} · {n}</button>;
          })}
        </div>
        {archivedCount > 0 && (
          <button className={"filter-chip" + (showArchived ? " on" : "")} onClick={() => setShowArchived((v: any) => !v)} title="Archived estimates stay out of the working list and out of follow-up">
            {showArchived ? "Hide" : "Show"} {archivedCount} archived
          </button>
        )}
        {/* EST-04: find a named customer without leaving the page */}
        <label className="est-search">
          <Icon name="search" size={14} />
          <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search customer, address, product or rep" aria-label="Search estimates" />
          {query && <button className="est-search-x" aria-label="Clear search" onClick={() => setQuery("")}><Icon name="x" size={13} /></button>}
        </label>
      </div>

      <div className={"card" + (view === "list" && list.length ? "" : " pad")} style={view === "list" && list.length ? { padding: "6px 14px 8px" } : undefined}>
        {list.length === 0
          ? quotes.length > 0
            /* filtered to nothing — say so. "No estimates yet" on a filter that
               happens to be empty reads as data loss on an account with five. */
            ? <EmptyState icon="send"
                title={needle ? `No estimates match "${query}"` : filter === "all" ? "Nothing to show" : `Nothing in ${(VIEWS.find(v => v.id === filter) || { label: filter }).label}`}
                sub={needle
                  ? `Searched customer, address, product and rep across ${quotes.length} estimates.`
                  : filter === "draft" ? `All ${quotes.length} of your estimates have been sent.`
                  : filter === "followup" ? "Nothing has been sitting unanswered for three days. "
                  : `None of your ${quotes.length} estimates are here right now.`}
                action={{ label: needle ? "Clear search" : "Show all estimates", icon: "undo", onClick: () => { setQuery(""); setFilter("all"); } }} />
            : lens === "concrete"
            ? <EmptyState icon="layers" title="No concrete estimates yet" sub="Type one in — what they need, the area, your price — and send it like any other quote." action={{ label: V.newEstimate, icon: "plus", onClick: () => go("estimator") }} />
            : <EmptyState icon="send" title="No estimates yet" sub="Build one in the Estimator and send it to a customer." action={{ label: "Open the Estimator", icon: "map", onClick: () => go("estimator") }} secondary={{ label: "Set up products first", onClick: () => go("products") }} />
          : view === "list"
          ? <DataTable rowKey="id" initialSort={{ key: "createdAt", dir: "desc" }} onRowClick={(q: any) => go("quote", { quoteId: q.id })}
              rows={list.map(q => { const m = measureOf(q); return { ...q, _qty: m.qty, _sends: sendsOf(q).length, _perFt: m.qty > 0 && q.total > 0 ? q.total / m.qty : null, _age: q.createdAt ? Math.max(0, Math.floor((Date.now() - q.createdAt) / 86400000)) : null }; })}
              columns={[
                { key: "customer", label: "Customer", render: (q: any) => <span className="dt-title">{q.customer || "Customer"} <ExpiredChip q={q} /></span> },
                { key: "productName", label: "Product", className: "ellipsis", render: (q: any) => <span className="muted">{q.productName || "—"}</span> },
                { key: "_qty", label: V.unitCol, num: true, render: (q: any) => Math.round(q._qty || 0) },
                { key: "_perFt", label: perUnitLabel(lens), num: true, render: (q: any) => q._perFt ? MP_money0(q._perFt) : "—" },
                { key: "total", label: "Total", num: true, render: (q: any) => <b style={{ color: "var(--teal)" }}>{MP_money0(q.total)}</b> },
                { key: "status", label: "Status", render: (q: any) => <QuoteState q={q} settings={store.settings} lost={lostReasonLabel(q.lostReason)} /> },
                { key: "createdAt", label: "Age", num: true, title: "Days since created", render: (q: any) => q._age == null ? "—" : <span title={fmt(q.createdAt)}>{q._age === 0 ? "today" : q._age + "d"}</span> },
                { key: "_sends", label: "Sends", num: true, render: (q: any) => q._sends || "—" },
                { key: "rep", label: "Rep", render: (q: any) => <span className="muted">{q.rep || "—"}</span> },
                { key: "_act", label: "", sort: false, num: true, render: (q: any) => <span className="dt-actions">
                    {expiredQ(q) && <ExtendBtn q={q} />}
                    {verticalOf(q) === "fence" && <button className="btn btn-ghost btn-sm" title="Job packet — blueprint, install specs & documents" onClick={() => setPacketQ(q)}><Icon name="book" size={13} /></button>}
                    <button className="btn btn-ghost btn-sm" style={{ color: "var(--red)" }} title={removeLabel(q) + " estimate"} onClick={() => removeQuote(q)}><Icon name="trash" size={13} /></button>
                  </span> },
              ]} />
          : <div style={{ display: "grid", gap: 9 }}>
              {list.map(q => {
                const sends = sendsOf(q);
                return (
                  <div key={q.id} className="proj-row quote-row" {...clickable(() => go("quote", { quoteId: q.id }), { label: `Open the estimate for ${q.customer || "this customer"}` })}>
                    {/* sizing lives in CSS, not inline: an inline `flex: 1` here
                        outranks the mobile stacking rule and crushed the name to
                        one word per line beside the buttons */}
                    <div className="quote-row-main">
                      <div className="quote-row-name">{q.customer || "Customer"}<ExpiredChip q={q} /></div>
                      <div className="muted quote-row-meta">{q.productName} · {fmtMeasure(q)}{measureOf(q).qty > 0 && q.total > 0 ? <span style={{ color: "var(--fg-2)", fontWeight: 600 }}> · {MP_money0(q.total / measureOf(q).qty)}/{measureOf(q).unit}</span> : null} · {fmt(q.createdAt)}{sends.length > 1 ? ` · sent ${sends.length}×` : ""}{q.status === "lost" ? <span style={{ color: "var(--red)" }}> · {lostReasonLabel(q.lostReason)}</span> : null}</div>
                    </div>
                    {/* the actions travel as one unit so they wrap under the name
                        together instead of squeezing it */}
                    <div className="quote-row-trail">
                      {expiredQ(q) && <ExtendBtn q={q} />}
                      {verticalOf(q) === "fence" && <button className="btn btn-ghost btn-sm" title="Open this estimate's job packet — blueprint, install specs & your documents (print / save as PDF)"
                        onClick={(e) => { e.stopPropagation(); setPacketQ(q); }}><Icon name="book" size={14} />Packet</button>}
                      <QuoteState q={q} settings={store.settings} lost={lostReasonLabel(q.lostReason)} />
                      <div className="display tnum quote-row-total">{MP_money0(q.total)}</div>
                      <button className="proj-del" title={removeLabel(q) + " estimate"} onClick={(e) => { e.stopPropagation(); removeQuote(q); }}><Icon name="trash" size={15} /></button>
                      <Icon name="chevright" size={16} className="muted quote-row-chev" />
                    </div>
                  </div>
                );
              })}
            </div>}
      </div>
      {packetQ && <FencePlanSheet quote={packetQ} store={store} settings={store.settings} onClose={() => setPacketQ(null)} />}
      {quick && <QuickEstimateModal store={store} go={go} mode={quick} onClose={() => setQuick(null)} />}
    </div>
  );
}

Object.assign(window, { EstimatesPage });

export { EstimatesPage, MP_money0 };
